import React, { useMemo } from "react";
import {
  Box, Breadcrumbs, Button, Card, Divider, Grid, IconButton, Link as MLink, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EditIcon from "@mui/icons-material/Edit";
import RateReviewIcon from "@mui/icons-material/RateReview";
import UndoIcon from "@mui/icons-material/Undo";
import BlockIcon from "@mui/icons-material/Block";
import ReplayIcon from "@mui/icons-material/Replay";
import PrintIcon from "@mui/icons-material/Print";
import {
  useGetSessionByIdQuery,
  useSessionCompleteMutation, useSessionSubmitReviewMutation,
  useSessionApproveMutation, useSessionReturnMutation,
  useSessionVoidMutation, useSessionRestoreMutation,
  useGetPromptTemplateQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { PageSurface, LegacyTokens, TypeScale, resolveStatusTriad, B13, B13Ink, tint12 } from "../../../theme/b13Tokens";
import { AbaSessionStatusChip } from "../shared/SessionBadges";

/**
 * Session detail — the clinical record of one session.
 *
 * Batch 50 rebuild. Uses the same accuracy ring + trial strip
 * components as the recorder in Batch 49 so the recording surface
 * and the review surface read as one product. Prompt distribution
 * uses the target's hierarchy colours; entries table is below the
 * fold as the audit trail; notes and audit each have their own card,
 * hidden when empty.
 */
export default function SessionView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetSessionByIdQuery(id!, { skip: !id });
  const bundle: any = data?.data;
  const s = bundle?.session;
  const entries: any[] = bundle?.entries || [];
  const audit: any[] = bundle?.audit || [];

  // Pull the target's prompt hierarchy so we can render prompt-level
  // colours on the strip and in the distribution bar. Mirrors the
  // recorder's Batch 49 resolution: target override wins over program.
  const effectivePromptTemplateId =
    s?.promptTemplateIdSnapshot ?? s?.promptTemplateId ?? null;
  const { data: hierData } = useGetPromptTemplateQuery(
    effectivePromptTemplateId ? { id: effectivePromptTemplateId } : {},
    { skip: !effectivePromptTemplateId }
  );
  const promptLevels: any[] = useMemo(() => {
    const rows: any[] = Array.isArray(hierData?.data) ? (hierData!.data as any[]) : [];
    const match = effectivePromptTemplateId
      ? rows.find((r) => String(r.id) === String(effectivePromptTemplateId))
      : rows[0];
    return match?.levels ?? [];
  }, [hierData, effectivePromptTemplateId]);

  // Batch 50 §2.2 — result figures. Compute from entries so the numbers
  // are honest, then fall back to the summary-row columns if entries
  // are absent (older sessions may be summary-only; §2.5 says the
  // entries card explains that state rather than pretending trials
  // will arrive).
  const trials = entries.filter((e) => e.entryType === "trial");
  const trialCount = trials.length || Number(s?.trialsCompleted ?? 0);
  const correctCount = trials.length
    ? trials.filter((e) => e.result === "correct").length
    : Number(s?.correctTrials ?? 0);
  const incorrectCount = trials.length
    ? trials.filter((e) => e.result === "incorrect").length
    : Math.max(0, trialCount - correctCount);
  const percent = trialCount > 0 ? Math.round((correctCount / trialCount) * 100) : 0;
  const promptCounts = useMemo(() => {
    const m = new Map<string, { level: any; count: number }>();
    for (const e of trials) {
      const key = String(e.promptLevelId ?? e.promptLevelName ?? "none");
      const lvl = promptLevels.find((p) => String(p.id) === String(e.promptLevelId)) ?? {
        levelName: e.promptLevelName ?? "Unspecified",
        colour: B13Ink.ink3,
        shortCode: null,
        countsAsIndependent: false,
      };
      const cur = m.get(key);
      if (cur) cur.count++;
      else m.set(key, { level: lvl, count: 1 });
    }
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [trials, promptLevels]);
  const mostUsedPrompt = promptCounts[0]?.level?.levelName ?? s?.promptLevelUsed ?? "—";
  const independentPct = useMemo(() => {
    if (!trials.length) return null;
    const indep = trials.filter((e) => {
      const lvl = promptLevels.find((p) => String(p.id) === String(e.promptLevelId));
      return lvl?.countsAsIndependent;
    }).length;
    return Math.round((indep / trials.length) * 100);
  }, [trials, promptLevels]);

  // Only render the transitions the controller actually implements
  // (Batch 50 §2.8): complete, submit-review, approve, return, void,
  // restore. Show only those that are legal for the current status.
  const [complete] = useSessionCompleteMutation();
  const [submitReview] = useSessionSubmitReviewMutation();
  const [approve] = useSessionApproveMutation();
  const [returnCorrection] = useSessionReturnMutation();
  const [voidSession] = useSessionVoidMutation();
  const [restoreSession] = useSessionRestoreMutation();

  const isWide = useMediaQuery("(min-width:1280px)");

  if (isLoading || !s) return (
    <Box sx={{ ...PageSurface, p: 4, textAlign: "center", color: B13Ink.ink3 }}>Loading…</Box>
  );

  const triad = resolveStatusTriad(s.status);

  const runAction = async (fn: () => Promise<any>, ok: string) => {
    try {
      const r: any = await fn();
      if (r?.status) { showMessage(r.message || ok); refetch(); }
      else showErrorMessage(r?.message || "Action failed.");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Action failed."); }
  };

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3 } }}>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
        <MLink underline="hover" component={Link} to="/aba-therapy/programs" sx={{ color: B13Ink.ink3 }}>ABA Therapy</MLink>
        <MLink underline="hover" component={Link} to="/aba-therapy/sessions" sx={{ color: B13Ink.ink3 }}>Session Data</MLink>
        <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Session #{s.id}</Typography>
      </Breadcrumbs>

      {/* Header — target-name-as-title, program/student/staff/date below.
         Left accent bar in status colour. */}
      <Card variant="outlined" sx={{
        p: 3, mb: 3,
        borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
        boxShadow: `inset 4px 0 0 0 ${triad.dot}`,
        backgroundColor: LegacyTokens.surface,
      }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} justifyContent="space-between">
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: LegacyTokens.tealSoft, color: LegacyTokens.teal,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <EventNoteIcon fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                {s.targetName || "Session"}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: B13Ink.ink2, mt: 0.5 }}>
                {s.programName}
              </Typography>
              <Typography sx={{ ...TypeScale.caption, mt: 0.25 }}>
                {[s.studentName, s.staffName, formatDate(s.sessionDate)].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <AbaSessionStatusChip status={s.status} />
            <Tooltip title="Print session (browser)" arrow>
              <IconButton onClick={() => window.print()} aria-label="Print"
                sx={{ color: B13Ink.ink3 }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* §2.2 — result figure row + accuracy ring. Bare figure row
           (Program Detail language), ring beside it. */}
        <Grid container spacing={3} sx={{ mt: 2 }} alignItems="center">
          <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
            <AccuracyRing percent={percent} count={trialCount} correct={correctCount} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <FigureCell value={trialCount === 0 ? "—" : `${percent}%`} label="Accuracy" />
              <FigureCell value={correctCount} label="Correct" />
              <FigureCell value={incorrectCount} label="Incorrect" />
              <FigureCell value={trialCount} label="Trials" />
              <FigureCell value={mostUsedPrompt} label="Most used prompt" />
              {independentPct != null && (
                <FigureCell value={`${independentPct}%`} label="Independent" />
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Voided / returned / review-note surface — critical
           information about the session's history at the top. */}
        {s.voidReason && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: `${LegacyTokens.radius.control}px`,
            border: `1px solid ${LegacyTokens.line}`, backgroundColor: LegacyTokens.redSoft }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: LegacyTokens.redText, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Voided
            </Typography>
            <Typography sx={{ fontSize: 13, color: B13Ink.ink2, mt: 0.25 }}>{s.voidReason}</Typography>
          </Box>
        )}
        {s.correctionReason && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: `${LegacyTokens.radius.control}px`,
            border: `1px solid ${LegacyTokens.line}`, backgroundColor: LegacyTokens.amberSoft }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: LegacyTokens.amberText, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Returned for correction
            </Typography>
            <Typography sx={{ fontSize: 13, color: B13Ink.ink2, mt: 0.25 }}>{s.correctionReason}</Typography>
          </Box>
        )}
      </Card>

      {/* §2.3 — Trial strip. */}
      {trials.length > 0 && (
        <Card variant="outlined" sx={{
          p: 3, mb: 3,
          borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
          backgroundColor: LegacyTokens.surface, boxShadow: "none",
        }}>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1.5 }}>
            Trials ({trials.length}) — in recorded order
          </Typography>
          <TrialStrip trials={trials} promptLevels={promptLevels} />
        </Card>
      )}

      {/* §2.4 — Prompt distribution. */}
      {promptCounts.length > 0 && (
        <Card variant="outlined" sx={{
          p: 3, mb: 3,
          borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
          backgroundColor: LegacyTokens.surface, boxShadow: "none",
        }}>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1.5 }}>
            Prompt distribution
          </Typography>
          <PromptDistribution counts={promptCounts} total={trials.length} independentPct={independentPct} />
        </Card>
      )}

      {/* §2.6 — Session notes. Only render when a note exists — an
         empty notes card would be noise. */}
      {s.notes && (
        <Card variant="outlined" sx={{
          p: 3, mb: 3,
          borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
          backgroundColor: LegacyTokens.surface, boxShadow: "none",
        }}>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1 }}>Session notes</Typography>
          <Typography sx={{ fontSize: 14, color: B13Ink.ink2, whiteSpace: "pre-wrap" }}>{s.notes}</Typography>
        </Card>
      )}

      {/* §2.5 — Entries table (below the visual summary). */}
      <Card variant="outlined" sx={{
        p: 3, mb: 3,
        borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
        backgroundColor: LegacyTokens.surface, boxShadow: "none",
      }}>
        <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1.5 }}>
          Entries ({trials.length})
        </Typography>
        {trials.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: B13Ink.ink3 }}>
            This session was recorded as a summary — no trial-level entries were captured.
            Summary shows {trialCount} trials, {correctCount} correct
            {trialCount > 0 ? ` (${percent}%)` : ""}.
          </Typography>
        ) : (
          <EntriesTable trials={trials} promptLevels={promptLevels} />
        )}
      </Card>

      {/* §2.7 — Audit timeline. Rendered only when non-empty. */}
      {audit.length > 0 && (
        <Card variant="outlined" sx={{
          p: 3, mb: 3,
          borderColor: LegacyTokens.line, borderRadius: `${LegacyTokens.radius.card}px`,
          backgroundColor: LegacyTokens.surface, boxShadow: "none",
        }}>
          <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mb: 1.5 }}>
            Audit trail
          </Typography>
          <AuditTimeline audit={audit} />
        </Card>
      )}

      {/* §2.8 — Actions. Only render transitions the controller
         implements AND that are legal from the current status. */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        <Button
          onClick={() => navigate("/aba-therapy/sessions")}
          sx={{ color: B13Ink.ink3, textTransform: "none" }}
        >Back to list</Button>
        {(s.status === "draft" || s.status === "returned_for_correction") && (
          <Button variant="outlined" startIcon={<EditIcon />}
            onClick={() => navigate(`/aba-therapy/sessions/${s.id}/edit`)}
            sx={{ textTransform: "none", color: B13.primary, borderColor: tint12(B13.primary),
              "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary } }}>
            Continue
          </Button>
        )}
        {s.status === "draft" && (
          <Button variant="contained" startIcon={<DoneAllIcon />}
            onClick={() => runAction(() => complete(s.id).unwrap(), "Session completed.")}
            sx={{ textTransform: "none", backgroundColor: LegacyTokens.emerald, "&:hover": { backgroundColor: LegacyTokens.emerald } }}>
            Complete Session
          </Button>
        )}
        {s.status === "completed" && (
          <Button variant="contained" startIcon={<RateReviewIcon />}
            onClick={() => runAction(() => submitReview(s.id).unwrap(), "Submitted for review.")}
            sx={{ textTransform: "none", backgroundColor: B13.primary, "&:hover": { backgroundColor: B13.primary } }}>
            Submit for Review
          </Button>
        )}
        {s.status === "awaiting_review" && (
          <>
            <Button variant="outlined" startIcon={<UndoIcon />}
              onClick={() => runAction(() => returnCorrection(s.id).unwrap(), "Returned for correction.")}
              sx={{ textTransform: "none", color: LegacyTokens.amberText, borderColor: LegacyTokens.amberSoft,
                "&:hover": { backgroundColor: LegacyTokens.amberSoft } }}>
              Return for Correction
            </Button>
            <Button variant="contained" startIcon={<DoneAllIcon />}
              onClick={() => runAction(() => approve(s.id).unwrap(), "Reviewed.")}
              sx={{ textTransform: "none", backgroundColor: LegacyTokens.emerald,
                "&:hover": { backgroundColor: LegacyTokens.emerald } }}>
              Mark Reviewed
            </Button>
          </>
        )}
        {s.status !== "voided" && (
          <Button variant="outlined" startIcon={<BlockIcon />}
            onClick={() => runAction(() => voidSession(s.id).unwrap(), "Session voided.")}
            sx={{ textTransform: "none", color: LegacyTokens.redText, borderColor: LegacyTokens.redSoft,
              "&:hover": { backgroundColor: LegacyTokens.redSoft } }}>
            Void
          </Button>
        )}
        {s.status === "voided" && (
          <Button variant="contained" startIcon={<ReplayIcon />}
            onClick={() => runAction(() => restoreSession(s.id).unwrap(), "Session restored.")}
            sx={{ textTransform: "none", backgroundColor: B13.primary,
              "&:hover": { backgroundColor: B13.primary } }}>
            Restore
          </Button>
        )}
      </Stack>
    </Box>
  );
}

// ─── Batch 50 §2.2 — figure cell (Program Detail bare-figure language) ─
const FigureCell: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
  <Box sx={{ minWidth: 96 }}>
    <Typography sx={{ fontSize: 28, fontWeight: 700, color: LegacyTokens.ink, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mt: 0.5 }}>{label}</Typography>
  </Box>
);

// ─── Reuse: the recorder's accuracy ring, reduced size ──────────────────
const AccuracyRing: React.FC<{ percent: number; count: number; correct: number }> = ({ percent, count, correct }) => {
  const size = 160;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent || 0));
  const dash = (clamped / 100) * circ;
  return (
    // Batch 51 §4a — same 0-state cleanup as the recorder's ring.
    <Box role="figure"
      aria-label={count === 0 ? "No trials recorded" : `${clamped} percent correct, ${correct} of ${count} trials`}
      sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={B13.canvas} strokeWidth={stroke} />
        {count > 0 && (
          <circle cx={size/2} cy={size/2} r={r} fill="none"
            stroke={LegacyTokens.emerald} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`} />
        )}
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center" }}>
        {count > 0 && (
          <Typography sx={{ fontSize: 40, fontWeight: 700, color: LegacyTokens.ink, lineHeight: 1 }}>
            {`${Math.round(clamped)}%`}
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, color: B13Ink.ink3, mt: count > 0 ? 0.5 : 0 }}>
          {correct} of {count} trials
        </Typography>
      </Box>
    </Box>
  );
};

// ─── §2.3 — trial strip with hover tooltip ─────────────────────────────
const TrialStrip: React.FC<{ trials: any[]; promptLevels: any[] }> = ({ trials, promptLevels }) => {
  const summaryText = trials.map((t, i) =>
    `Trial ${i + 1}: ${t.result}${t.promptLevelName ? `, prompt ${t.promptLevelName}` : ""}`
  ).join(". ");
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap
      aria-label={`Trial sequence: ${summaryText}`}>
      {trials.map((t, i) => {
        const good = t.result === "correct";
        const bg = good ? LegacyTokens.emerald : LegacyTokens.red;
        const lvl = promptLevels.find((p) => String(p.id) === String(t.promptLevelId));
        const shortCode = (lvl?.shortCode ?? t.promptLevelName ?? "").toString().slice(0, 3).toUpperCase();
        const tip = `Trial ${i + 1} · ${good ? "Correct" : "Incorrect"}${lvl?.levelName ? ` · ${lvl.levelName}` : t.promptLevelName ? ` · ${t.promptLevelName}` : ""}${t.occurredAt ? ` · ${formatTime(t.occurredAt)}` : ""}`;
        return (
          <Tooltip key={t.id ?? i} title={tip} arrow>
            <Box aria-hidden="true" sx={{
              width: 28, height: 28,
              borderRadius: `${LegacyTokens.radius.chip}px`,
              backgroundColor: bg, color: LegacyTokens.surface,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 700,
              cursor: "default",
            }}>{shortCode}</Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

// ─── §2.4 — single horizontal stacked bar + caption ────────────────────
const PromptDistribution: React.FC<{
  counts: Array<{ level: any; count: number }>;
  total: number;
  independentPct: number | null;
}> = ({ counts, total, independentPct }) => (
  <Box>
    <Box sx={{
      display: "flex", width: "100%", height: 20,
      borderRadius: 4, overflow: "hidden",
      backgroundColor: B13.canvas,
    }}>
      {counts.map(({ level, count }, i) => (
        <Tooltip key={i} title={`${level.levelName}: ${count} trial${count === 1 ? "" : "s"}`} arrow>
          <Box sx={{
            width: `${(count / total) * 100}%`,
            backgroundColor: level.colour || B13Ink.ink3,
            transition: "none",
          }} />
        </Tooltip>
      ))}
    </Box>
    <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
      {counts.map(({ level, count }, i) => (
        <Stack key={i} direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%",
            backgroundColor: level.colour || B13Ink.ink3 }} />
          <Typography sx={{ fontSize: 12, color: B13Ink.ink2 }}>
            {count} {level.levelName.toLowerCase()}
          </Typography>
        </Stack>
      ))}
    </Stack>
    {independentPct != null && (
      <Typography sx={{ fontSize: 12, color: B13Ink.ink3, mt: 1.5, display: "block" }}>
        <strong style={{ color: B13Ink.ink2 }}>{independentPct}% independent</strong> — the figure
        mastery criteria evaluate against.
      </Typography>
    )}
  </Box>
);

// ─── §2.5 — Entries table ──────────────────────────────────────────────
const EntriesTable: React.FC<{ trials: any[]; promptLevels: any[] }> = ({ trials, promptLevels }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ "& td, & th": { borderBottomColor: LegacyTokens.line } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...TypeScale.tableHeader, width: 60 }}>#</TableCell>
          <TableCell sx={{ ...TypeScale.tableHeader, width: 130 }}>Outcome</TableCell>
          <TableCell sx={{ ...TypeScale.tableHeader }}>Prompt level</TableCell>
          <TableCell sx={{ ...TypeScale.tableHeader, width: 180 }}>Timestamp</TableCell>
          <TableCell sx={{ ...TypeScale.tableHeader }}>Notes</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {trials.map((t, i) => {
          const good = t.result === "correct";
          const lvl = promptLevels.find((p) => String(p.id) === String(t.promptLevelId));
          const colour = lvl?.colour || B13Ink.ink3;
          return (
            <TableRow key={t.id ?? i} sx={{ "& td": { py: 1.25 } }}>
              <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink3 }}>{i + 1}</TableCell>
              <TableCell>
                <Box component="span" sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.75,
                  backgroundColor: good ? LegacyTokens.emeraldSoft : LegacyTokens.redSoft,
                  color: good ? LegacyTokens.emeraldText : LegacyTokens.redText,
                  fontSize: 12, fontWeight: 600,
                  px: 1, py: 0.25,
                  borderRadius: `${LegacyTokens.radius.chip}px`,
                }}>
                  <Box component="span" sx={{
                    width: 7, height: 7, borderRadius: "50%",
                    backgroundColor: good ? LegacyTokens.emerald : LegacyTokens.red,
                  }} />
                  {good ? "Correct" : "Incorrect"}
                </Box>
              </TableCell>
              <TableCell>
                {(lvl?.levelName || t.promptLevelName) ? (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: colour }} />
                    <Typography sx={{ fontSize: 13, color: B13Ink.ink2 }}>
                      {lvl?.levelName || t.promptLevelName}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 13, color: B13Ink.ink3 }}>—</Typography>
                )}
              </TableCell>
              <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink3 }}>
                {t.occurredAt ? new Date(t.occurredAt).toLocaleString() : "—"}
              </TableCell>
              <TableCell sx={{ ...TypeScale.tableCell, color: B13Ink.ink2 }}>
                {t.note || "—"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Box>
);

// ─── §2.7 — Audit timeline ─────────────────────────────────────────────
const ACTION_LABEL: Record<string, string> = {
  created: "Created", completed: "Completed",
  submitted_review: "Submitted for review",
  reviewed: "Reviewed", returned_for_correction: "Returned for correction",
  voided: "Voided", restored: "Restored",
};
const AuditTimeline: React.FC<{ audit: any[] }> = ({ audit }) => (
  <Stack spacing={1.25}>
    {audit.map((a) => (
      <Stack key={a.id} direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{
          width: 8, height: 8, mt: 0.75,
          borderRadius: "50%", backgroundColor: B13.primary,
          flexShrink: 0,
        }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: B13Ink.ink2 }}>
            {ACTION_LABEL[a.action] ?? a.action}
          </Typography>
          <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>
            {formatDateTime(a.happenedAt)}{a.actorId ? ` · by user #${a.actorId}` : ""}
            {a.note ? ` · ${a.note}` : ""}
          </Typography>
        </Box>
      </Stack>
    ))}
  </Stack>
);

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
function formatTime(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
